import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from 'react';

import {
  publishCanonicalTreeHistoricalAuthorityPresentationObservability,
} from '@/governor/tree/CanonicalTreeHistoricalAuthorityPresentationObservability';

interface TreeHistoricalAuthorityPresentationErrorBoundaryProps {
  readonly children:
    ReactNode;
}

interface TreeHistoricalAuthorityPresentationErrorBoundaryState {
  readonly failed:
    boolean;

  readonly message:
    string | null;
}

export default class TreeHistoricalAuthorityPresentationErrorBoundary
  extends Component<
    TreeHistoricalAuthorityPresentationErrorBoundaryProps,
    TreeHistoricalAuthorityPresentationErrorBoundaryState
  > {
  state:
  TreeHistoricalAuthorityPresentationErrorBoundaryState = {
    failed:
      false,

    message:
      null,
  };

  static getDerivedStateFromError(
    error:
      unknown,
  ): TreeHistoricalAuthorityPresentationErrorBoundaryState {
    return {
      failed:
        true,

      message:
        error instanceof Error
          ? error.message
          : String(error),
    };
  }

  componentDidCatch(
    error:
      unknown,

    info:
      ErrorInfo,
  ): void {
    publishCanonicalTreeHistoricalAuthorityPresentationObservability({
      eventKind:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_RENDER_FAILURE',

      reason:
        'CARD_RENDER_FAILURE',

      failureMessage:
        error instanceof Error
          ? error.message
          : String(error),
    });

    console.error(
      'Tree historical-authority presentation failed safely.',
      {
        error,
        componentStack:
          info.componentStack,
      },
    );
  }

  render(): ReactNode {
    if (this.state.failed) {
      return (
        <section className="mt-5 rounded-[2rem] border border-slate-300 bg-slate-100 p-6 text-slate-950">
          <p className="text-xs font-bold uppercase tracking-[0.18em]">
            Historical authority
          </p>

          <h2 className="mt-2 text-3xl font-bold">
            Historical authority presentation unavailable
          </h2>

          <p className="mt-2 max-w-3xl text-sm">
            The historical repository-authority card failed safely. Registry evidence, structural repair review, and live Tree output remain available.
          </p>

          {this.state.message ? (
            <p className="mt-4 break-words rounded-xl border border-slate-300 bg-white/70 p-3 text-xs">
              Internal presentation error: {this.state.message}
            </p>
          ) : null}

          <p className="mt-5 text-xs opacity-75">
            Internal Governor fallback only. This state does not certify Tree health, runtime correctness, operational certification, deployment readiness, or release readiness.
          </p>
        </section>
      );
    }

    return this.props.children;
  }
}
